import React from 'react';
import cx from 'classnames';
import lexemes from 'components/lexemes';
import A11yVisuallyHidden from 'components/a11y/a11y-visually-hidden';
import Button from 'components/button/button';
import PageSection from './page-section';
import Icon, { IconName } from 'components/icon/icon';
import GeoContext from 'lib/geo-context';
import LocaleContext from 'lib/locale-context';
import { SupportedCountries } from 'lib/supported-countries';

import Link from 'components/link/link';
import LanguageSelector from 'components/language-selector/language-selector';
import { SupportedLanguage } from 'lib/lexemes/supported-languages';
import { Lexeme } from '@bumble/localization/components';

const { footer } = lexemes;
const generalLinks = (lang: SupportedLanguage) => [
    {
        title: footer.lxFooterPageLinksAbout,
        url: 'https://badoo.com/team/',
    },
    {
        title: footer.lxFooterPageLinksCareers,
        url: 'https://bumble.wd3.myworkdayjobs.com/Bumble_Careers',
    },
    { title: footer.lxFooterPageLinksBlog, url: `https://badoo.com/${lang}/the-blog` },
];

const socialLinks = [
    {
        id: 'instagram',
        title: footer.lxFooterSocialLinksInstagram,
        url: 'https://www.instagram.com/badoo/',
    },
    {
        id: 'facebook',
        title: footer.lxFooterSocialLinksFacebook,
        url: 'https://www.facebook.com/badoo/',
    },
    { id: 'twitter', title: footer.lxFooterSocialLinksTwitter, url: 'https://twitter.com/Badoo' },
];

const supportLinks = (lang: SupportedLanguage) => [
    { title: footer.lxFooterLegalLinksTerms, url: `https://badoo.com/${lang}/terms/` },
    { title: footer.lxFooterLegalLinksPrivacy, url: `https://badoo.com/${lang}/privacy/` },
    {
        title: footer.lxFooterLegalLinksCookiePolicy,
        url: `https://badoo.com/${lang}/cookie-policy/`,
        onClick: true,
    },
    { title: footer.lxFooterLegalLinksHelp, url: `https://badoo.com/${lang}/help/` },
    { title: footer.lxFooterLegalLinksPress, url: `https://badoo.com/${lang}/team/press/` },
];

const instagramMarketLinks = {
    [SupportedCountries.UNKNOWN]: `https://www.instagram.com/badoo/`,
    [SupportedCountries.UNITED_KINGDOM]: `https://www.instagram.com/badoo/`,
    [SupportedCountries.GERMANY]: `https://www.instagram.com/badoo/`,
    [SupportedCountries.FRANCE]: `https://www.instagram.com/badoo_fr/`,
    [SupportedCountries.RUSSIA]: `https://www.instagram.com/badoo_ru/`,
    [SupportedCountries.BRAZIL]: `https://www.instagram.com/badoo_br/`,
    [SupportedCountries.SPAIN]: `https://www.instagram.com/badoo_es/`,
};

const PageFooter: React.FC<{ narrow?: boolean }> = ({ narrow }) => {
    const classnames = cx({
        'page-footer': true,
        'is-narrow': narrow,
    });
    const lang = React.useContext(LocaleContext) as SupportedLanguage;
    const country = React.useContext(GeoContext) as SupportedCountries | null;

    // @TODO: setup in separate function
    let instagramLink = country
        ? instagramMarketLinks[country]
        : instagramMarketLinks[SupportedCountries.UNKNOWN];

    if (lang && instagramLink) {
        instagramLink += `?hl=${lang}`;
    }

    const instagram = socialLinks.filter((socialLink) => {
        return socialLink.id === 'instagram';
    })[0];

    instagram.url = instagramLink;

    return (
        <div className={classnames}>
            <A11yVisuallyHidden tag="h2">{footer.lxFooterA11yHeading()}</A11yVisuallyHidden>
            <PageSection extraClass="page-footer__general">
                <section className="page-footer__column">
                    <div className="page-footer__cta">
                        <Button
                            color={Button.color.White}
                            designType={Button.designType.FILLED}
                            size={Button.size.STRETCH}
                            text={footer.lxFooterCtaCreateAccount()}
                            href="https://badoo.com/signup"
                            target="_blank"
                        />
                    </div>

                    <div className="page-footer__language-selector">
                        <LanguageSelector />
                    </div>
                </section>

                <section className="page-footer__column" aria-labelledby="footer-section-2">
                    <h3 className="page-footer__column-title" id="footer-section-2">
                        {footer.lxFooterPageLinksGeneral()}
                    </h3>
                    <ul className="page-footer__column-list" role="list">
                        {generalLinks(lang).map((item, index) => (
                            <li key={index}>
                                <Link href={item.url} target="_blank">
                                    <span className="page-footer__column-list-link">
                                        {item.title()}

                                        <A11yVisuallyHidden>
                                            <Lexeme path="mw@link.a11y.label.new-window" />
                                        </A11yVisuallyHidden>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="page-footer__column" aria-labelledby="footer-section-3">
                    <h3 className="page-footer__column-title" id="footer-section-3">
                        {footer.lxFooterSocialLinksFollow()}
                    </h3>
                    <ul className="page-footer__column-list" role="list">
                        {socialLinks.map((item, index) => (
                            <li key={index}>
                                <Link href={item.url} target="_blank">
                                    <span className="page-footer__column-list-link">
                                        {item.title()}

                                        <A11yVisuallyHidden>
                                            <Lexeme path="mw@link.a11y.label.new-window" />
                                        </A11yVisuallyHidden>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </section>

                <section className="page-footer__column" aria-labelledby="footer-section-4">
                    <h3 className="page-footer__column-title" id="footer-section-4">
                        {footer.lxFooterCtaGetTheApp()}
                    </h3>
                    <div className="page-footer__mobile">
                        <a
                            href="https://bdo.to/u/3eTXW2d"
                            target="_blank"
                            className="page-footer__mobile-button is-ios"
                        >
                            <Icon
                                name={IconName.AppStoreButton}
                                a11y={{ label: footer.lxFooterCtaAppStore() }}
                            />

                            <A11yVisuallyHidden>
                                <Lexeme path="mw@link.a11y.label.new-window" />
                            </A11yVisuallyHidden>
                        </a>
                        <a
                            href="https://bdo.to/u/3eTXW2d"
                            target="_blank"
                            className="page-footer__mobile-button is-android"
                        >
                            <Icon
                                name={IconName.GooglePlayButton}
                                a11y={{ label: footer.lxFooterCtaGooglePlay() }}
                            />

                            <A11yVisuallyHidden>
                                <Lexeme path="mw@link.a11y.label.new-window" />
                            </A11yVisuallyHidden>
                        </a>
                    </div>
                </section>
            </PageSection>

            <PageSection extraClass="page-footer__support" tag="section">
                <div className="page-footer__logo">
                    <a href="http://badoo.com" target="_blank">
                        <Icon
                            name={IconName.Badoo}
                            a11y={{ label: footer.lxFooterLogoA11yLabel({ partner: 'Badoo' }) }}
                        />

                        <A11yVisuallyHidden>
                            <Lexeme path="mw@link.a11y.label.new-window" />
                        </A11yVisuallyHidden>
                    </a>
                    <span className="page-footer__logo-credits">
                        {footer.lxFooterLogoTitle({ page_parter_name: 'Badoo' })}
                    </span>
                </div>

                <nav className="page-footer__legal" aria-label={footer.lxFooterA11yHeading()}>
                    <ul className="page-footer__links" role="list">
                        {supportLinks(lang).map((item, index) => (
                            <li key={index}>
                                {item.onClick ? (
                                    <button className="js-manage-cookies">{item.title()}</button>
                                ) : (
                                    <Link href={item.url} target="_blank">
                                        <span>
                                            {item.title()}

                                            <A11yVisuallyHidden>
                                                <Lexeme path="mw@link.a11y.label.new-window" />
                                            </A11yVisuallyHidden>
                                        </span>
                                    </Link>
                                )}
                            </li>
                        ))}
                    </ul>

                    <div className="page-footer__copyright">
                        {footer.lxFooterCopyright({
                            current_year: new Date().getFullYear().toString(),
                        })}
                    </div>
                </nav>
            </PageSection>
        </div>
    );
};

export default PageFooter;
